/**
 * Composition root.
 *
 * This file wires; it does not implement. Screens, the design system, routing,
 * i18n and the API client all live in workspace packages, and this only
 * assembles them — the same rule the Flutter base template applies to its
 * `lib/` directory, and the reason a fork can replace a feature without
 * touching the entry point.
 *
 * The provider order is not arbitrary. Reading outwards to inwards:
 *
 *   ErrorBoundary → Theme → i18n → Toaster → Session → Access → Router
 *
 * - **ErrorBoundary outermost**, so a crash in any provider still renders
 *   something rather than a white page.
 * - **Theme above everything visual**, so even a fallback rendered by the
 *   boundary is styled.
 * - **Toaster above Session**, because signing out raises a notification, and
 *   a provider cannot use a context declared beneath it.
 * - **Session above Access**, because permissions are fetched *for* a
 *   principal: with no session there is nobody to fetch them for.
 * - **Router innermost**, because its gates read all of the above.
 */

import { useEffect, useState } from "react";
import { RouterProvider } from "@tanstack/react-router";
import { I18nextProvider, useTranslation } from "react-i18next";
import type { i18n as I18n } from "i18next";

import { AccessProvider, useAccess } from "@hardauto/core-access";
import {
  applyLocaleToDocument,
  createI18n,
  initialLocale,
  registerCommonNamespace,
} from "@hardauto/core-i18n";
import { createAppRouter, type RouterContext } from "@hardauto/core-router";
import { LoadingState, ThemeProvider, Toaster } from "@hardauto/core-ui";
import { ErrorBoundary, NotFoundScreen, ServerErrorScreen } from "@hardauto/feature-errors";
import { SessionProvider, useSession } from "@hardauto/feature-iam";

import { AppLayout } from "./layout";
import { client } from "./api";
import { config } from "./config";
import { usePermissions } from "./permissions";
import { routeTable } from "./routes";
import { WorkspaceProvider } from "./workspace";

registerCommonNamespace();

export function App() {
  const [i18n, setI18n] = useState<I18n | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const locale = initialLocale(navigator.languages);
      const instance = await createI18n(locale);
      // Sets `lang` and `dir` on <html>. Without `dir`, Arabic renders
      // left-to-right and the layout is wrong rather than merely untranslated.
      applyLocaleToDocument(locale);
      if (!cancelled) setI18n(instance);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!i18n) {
    // Deliberately untranslated: this is the moment before translations exist,
    // and a raw key on screen would look worse than one neutral English word
    // for a few milliseconds.
    return <LoadingState label="Loading" />;
  }

  return (
    <ErrorBoundary
      fallback={(error) => <ServerErrorScreen correlationId={error.message} />}
      onError={(error) => {
        // Reported to the console for now. A fork points this at whatever it
        // uses — the seam exists so that adding one is a one-line change here
        // rather than a hunt through the component tree.
        console.error("Unhandled render failure", error);
      }}
    >
      <ThemeProvider>
        <I18nextProvider i18n={i18n}>
          <Toaster>
            <SessionProvider client={client} deviceName={config.deviceName}>
              <Principal />
            </SessionProvider>
          </Toaster>
        </I18nextProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

/**
 * Fetches the principal's permissions and publishes them.
 *
 * A component rather than a hook inside `App`, because it has to sit *below*
 * `SessionProvider` to read the session — and `App` is where that provider is
 * declared.
 */
function Principal() {
  const session = useSession();
  const authenticated = session.status === "authenticated";
  // Asking before there is a principal produces a guaranteed 401 on every page
  // load: noise in the logs and a wasted round trip.
  const { permissions, ready } = usePermissions(client, authenticated);

  return (
    <AccessProvider permissions={permissions} ready={ready}>
      {/* Above the router, so the header band and every screen read the same
          context; inside the access provider, so the panel knows what this
          principal may list. */}
      <WorkspaceProvider>
        <RouterHost sessionReady={session.status !== "restoring"} authenticated={authenticated} />
      </WorkspaceProvider>
    </AccessProvider>
  );
}

/**
 * Decides whether there is an answer to give the router yet.
 *
 * Two ordering problems live here, and both were found by loading
 * `/admin/users` directly rather than clicking to it — the click always
 * worked, which is what made the first one look like a permissions bug.
 *
 * 1. **The gates can only redirect, so "not known yet" must never reach
 *    them.** Redirecting for that throws the URL away: a deep link becomes a
 *    spinner the user cannot leave. Holding the router unmounted costs nothing
 *    by comparison — the browser URL is untouched, so the router re-reads it
 *    and re-evaluates the moment it appears.
 *
 * 2. **The router must not EXIST before the principal does.** It owns the
 *    history, so merely creating it and calling `invalidate` is enough to
 *    rewrite the URL — which is what happened when it was built at module
 *    scope, or built here and then updated: its first evaluation ran against
 *    an empty context and sent a permitted user to `/loading`. Splitting the
 *    mount into two components is what guarantees the router is constructed
 *    on a render that already has the real context.
 */
function RouterHost({
  sessionReady,
  authenticated,
}: {
  sessionReady: boolean;
  authenticated: boolean;
}) {
  const { t } = useTranslation("errors");
  const { snapshot, ready } = useAccess();

  if (!sessionReady || (authenticated && !ready)) {
    return <LoadingState label={t("loading.restoring")} />;
  }

  return <MountedRouter context={{ sessionReady, authenticated, accessReady: ready, snapshot }} />;
}

/**
 * The router itself, created on its first render — which, by construction, is
 * a render that already knows who the principal is.
 */
function MountedRouter({ context }: { context: RouterContext }) {
  // `useState` with an initialiser, not `useMemo`: this must be created
  // exactly once for the life of the component, and `useMemo` is explicitly
  // allowed to discard its value.
  const [router] = useState(() =>
    createAppRouter({
      routes: routeTable,
      rootComponent: AppLayout,
      notFoundComponent: NotFoundScreen,
      context,
    }),
  );

  // Re-run the gates when the principal changes — a role granted, a session
  // revoked from another device.
  useEffect(() => {
    router.update({ context });
    void router.invalidate();
  }, [router, context]);

  return <RouterProvider router={router} context={context} />;
}
